import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import "../styles/tenant-metrics.css";
import {
  searchAdminTenants,
  getOnboardingConversionMetrics,
  getUserMetrics,
  getTenantListFilterOptions,
} from "../context/functions/AdminFunction";
import TenantListWithMetrics from "../components/tenants/TenantListWithMetrics";
import { X, Search, RefreshCw } from "lucide-react";

const formatMedianTime = (seconds) => {
  const totalSeconds = Math.round(seconds);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

const TenantMetrics = () => {

  const [filters, setFilters] = useState({
    datePreset: "month",
    startDate: moment().startOf("month").format("YYYY-MM-DD"),
    endDate: moment().endOf("month").format("YYYY-MM-DD"),
    tenantId: "",
    tenantSearch: "",
  });
  const [showDateRangeMenu, setShowDateRangeMenu] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    nationalities: [],
    employment_statuses: [],
    landlords: [],
  });
  const [selectedFilters, setSelectedFilters] = useState({
    employmentStatuses: [],
    nationalities: [],
    landlordIds: [],
  });

  const [tenantSearchResults, setTenantSearchResults] = useState([]);
  const [showTenantDropdown, setShowTenantDropdown] = useState(false);
  const [searchingTenants, setSearchingTenants] = useState(false);
  const searchRef = useRef(null);
  const dateRangeRef = useRef(null);
  const filterPanelRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [openFilterMenu, setOpenFilterMenu] = useState(null);
  const [landlordSearchTerm, setLandlordSearchTerm] = useState("");

  const [tenantMetrics, setTenantMetrics] = useState({
    loading: false,
    data: null,
  });
  const [userMetrics, setUserMetrics] = useState({
    loading: false,
    data: null,
  });
  const metricsAbortControllerRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchFilterOptions = async () => {
      try {
        const response = await getTenantListFilterOptions(controller.signal);
        if (response?.status === "canceled") return;
        if (response?.success && response?.data) {
          setFilterOptions({
            nationalities: response.data.nationalities || [],
            employment_statuses: response.data.employment_statuses || [],
            landlords: response.data.landlords || [],
          });
        }
      } catch (error) {
        console.error("Failed to fetch tenant filter options", error);
      }
    };

    fetchFilterOptions();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (metricsAbortControllerRef.current) metricsAbortControllerRef.current.abort();
    };
  }, []);

  const fetchTenantMetricsData = async () => {
    if (metricsAbortControllerRef.current) {
      metricsAbortControllerRef.current.abort();
    }

    const controller = new AbortController();
    metricsAbortControllerRef.current = controller;

    setTenantMetrics((prev) => ({ ...prev, loading: true }));
    setUserMetrics((prev) => ({ ...prev, loading: true }));
    try {
      const options = {};
      if (filters.startDate) options.start_date = moment(filters.startDate).startOf("day").toISOString();
      if (filters.endDate) options.end_date = moment(filters.endDate).endOf("day").toISOString();
      if (filters.tenantId) options.tenant_id = filters.tenantId;
      options.nationality = selectedFilters.nationalities;
      options.employment_status = selectedFilters.employmentStatuses;
      options.landlord_id = selectedFilters.landlordIds;
      options.signal = controller.signal;

      const userOptions = {};
      if (filters.startDate) userOptions.start_date = moment(filters.startDate).startOf("day").toISOString();
      if (filters.endDate) userOptions.end_date = moment(filters.endDate).endOf("day").toISOString();
      userOptions.nationality = selectedFilters.nationalities;
      userOptions.employment_status = selectedFilters.employmentStatuses;
      userOptions.landlord_id = selectedFilters.landlordIds;
      userOptions.signal = controller.signal;

      const [onboardingRes, userRes] = await Promise.all([
        getOnboardingConversionMetrics(options),
        getUserMetrics(userOptions),
      ]);

      setTenantMetrics({
        loading: false,
        data: {
          onboarding: onboardingRes?.data,
        },
      });
      setUserMetrics({
        loading: false,
        data: userRes?.data,
      });
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        return;
      }
      console.error("Failed to fetch tenant metrics", error);
      setTenantMetrics({ loading: false, data: null });
      setUserMetrics({ loading: false, data: null });
      toast.error("Failed to load tenant metrics");
    }
  };

  useEffect(() => {
    fetchTenantMetricsData();
  }, [
    filters.startDate,
    filters.endDate,
    filters.tenantId,
    selectedFilters.nationalities,
    selectedFilters.employmentStatuses,
    selectedFilters.landlordIds,
  ]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowTenantDropdown(false);
      }
      if (
        dateRangeRef.current &&
        !dateRangeRef.current.contains(event.target)
      ) {
        setShowDateRangeMenu(false);
      }
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(event.target)
      ) {
        setOpenFilterMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getDateRangeLabel = (preset) => {
    if (preset === "") return "All Time";
    if (preset === "month") return "Month";
    if (preset === "custom") return "Custom";
    return "Week";
  };

  const handleFilterChange = (name, value) => {
    if (name === "datePreset") {
      if (!value) {
        setFilters((prev) => ({
          ...prev,
          datePreset: "",
          startDate: "",
          endDate: "",
        }));
        return;
      }
      if (value === "custom") {
        setFilters((prev) => ({
          ...prev,
          datePreset: "custom",
          startDate: "",
          endDate: "",
        }));
        return;
      }
      const startDate = moment().startOf(value).format("YYYY-MM-DD");
      const endDate = moment().endOf(value).format("YYYY-MM-DD");
      setFilters((prev) => ({
        ...prev,
        datePreset: value,
        startDate,
        endDate,
      }));
      return;
    }
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilters((prev) => ({ ...prev, tenantSearch: value }));

    if (value.length > 2) {
      setSearchingTenants(true);
      setShowTenantDropdown(true);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(async () => {
        try {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          const controller = new AbortController();
          abortControllerRef.current = controller;

          const response = await searchAdminTenants(value, controller.signal);

          if (response?.status === "canceled") return;

          if (response && response.success && response.data) {
            setTenantSearchResults(response.data);
          } else {
            setTenantSearchResults([]);
          }
        } catch (error) {
          console.error("Error searching tenants:", error);
          setTenantSearchResults([]);
        } finally {
          setSearchingTenants(false);
        }
      }, 500);
    } else {
      setTenantSearchResults([]);
      setShowTenantDropdown(false);
    }
  };

  const handleSelectTenant = (tenant) => {
    setFilters((prev) => ({
      ...prev,
      tenantId: tenant._id,
      tenantSearch: tenant.email || `${tenant.fname} ${tenant.lname}`,
    }));
    setShowTenantDropdown(false);
  };

  const clearTenantFilter = () => {
    setFilters((prev) => ({
      ...prev,
      tenantId: "",
      tenantSearch: "",
    }));
    setTenantSearchResults([]);
  };

  const formatRate = (value) => {
    const numericValue = Number(value || 0);
    const percentValue = numericValue <= 1 ? numericValue * 100 : numericValue;
    return `${Number(percentValue.toFixed(1)).toString()}%`;
  };

  const toggleMultiValue = (key, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter((item) => item !== value)
        : [...prev[key], value],
    }));
  };

  const clearFilter = (key) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [key]: [],
    }));
  };

  const getFilterSummary = (values, label) => {
    if (!values.length) return `All ${label}`;
    if (values.length === 1) return values[0];
    return `${values.length} selected`;
  };

  const filteredLandlords = filterOptions.landlords.filter((landlord) => {
    if (!landlordSearchTerm.trim()) return true;
    const term = landlordSearchTerm.toLowerCase();
    return (
      (landlord.name || "").toLowerCase().includes(term) ||
      (landlord.email || "").toLowerCase().includes(term)
    );
  });

  const getStatusLabel = (status) => {
    const clean = status?.split('_').join(' ');
    const first = clean.split('')[0];
    const rest = clean.slice(1);
    return first.toUpperCase() + rest;
  }

  return (
    <div className="tenant-metrics-container">
      <h4 className="fw-bold mb-1" style={{ color: "#2c3e50", fontSize: "22px" }}>
        Tenant Metrics
      </h4>
      <p className="text-muted small fw-medium mb-0">
        Track tenant onboarding, engagement, and documents
      </p>
      <div className="filters-inline-wrap mt-4">
      {/* range filter */}
      <div className="date-filter" ref={dateRangeRef}>
        <div className="range-selector">
          {/* <span className="range-label">Range</span> */}
          <button
            type="button"
            className="range-chip"
            onClick={() => setShowDateRangeMenu((prev) => !prev)}
          >
            {getDateRangeLabel(filters.datePreset)}
          </button>
          {showDateRangeMenu && (
            <div className="range-menu">
              {[
                { value: "", label: "All Time" },
                { value: "week", label: "Week" },
                { value: "month", label: "Month" },
                { value: "custom", label: "Custom" },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`range-menu-item ${
                    filters.datePreset === option.value
                      ? "range-menu-item-active"
                      : ""
                  }`}
                  onClick={() => {
                    handleFilterChange("datePreset", option.value);
                    setShowDateRangeMenu(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

{/* filters */}
      <div className="tenant-filter-panel" ref={filterPanelRef}>
        <div className="tenant-filter-row">
          <div className="tenant-filter-group">
            {/* <label className="range-label">
              Employment Status
            </label> */}
            <button
              type="button"
              className={`range-chip range-chip-filter ${openFilterMenu === "employmentStatuses" ? "range-chip-filter-active" : ""}`}
              onClick={() =>
                setOpenFilterMenu((prev) =>
                  prev === "employmentStatuses" ? null : "employmentStatuses",
                )
              }
            >
              <span>{getFilterSummary(selectedFilters.employmentStatuses, "statuses")}</span>
            </button>
            {openFilterMenu === "employmentStatuses" && (
              <div className="tenant-filter-dropdown">
                <div className="tenant-filter-dropdown-list">
                  {filterOptions.employment_statuses
                  .filter((s)=>s)
                  .map((status) => (
                    <label key={status} className="tenant-filter-option">
                      <input
                        type="checkbox"
                        checked={selectedFilters.employmentStatuses.includes(status)}
                        onChange={() => toggleMultiValue("employmentStatuses", status)}
                      />
                      {/* capitalize first word */}
                      <span>{getStatusLabel(status)}</span>
                    </label>
                  ))}
                </div>
                <div className="tenant-filter-actions">
                  <button type="button" onClick={() => clearFilter("employmentStatuses")}>
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="tenant-filter-group">
            {/* <label className="range-label">
              Nationality
            </label> */}
            <button
              type="button"
              className={`range-chip range-chip-filter ${openFilterMenu === "nationalities" ? "range-chip-filter-active" : ""}`}
              onClick={() =>
                setOpenFilterMenu((prev) =>
                  prev === "nationalities" ? null : "nationalities",
                )
              }
            >
              <span>{getFilterSummary(selectedFilters.nationalities, "nationalities")}</span>
            </button>
            {openFilterMenu === "nationalities" && (
              <div className="tenant-filter-dropdown">
                <div className="tenant-filter-dropdown-list">
                  {filterOptions.nationalities.map((country) => (
                    <label key={country} className="tenant-filter-option">
                      <input
                        type="checkbox"
                        checked={selectedFilters.nationalities.includes(country)}
                        onChange={() => toggleMultiValue("nationalities", country)}
                      />
                      <span>{country}</span>
                    </label>
                  ))}
                </div>
                <div className="tenant-filter-actions">
                  <button type="button" onClick={() => clearFilter("nationalities")}>
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="tenant-filter-group">
            {/* <label className="range-label">
              Landlord
            </label> */}
            <button
              type="button"
              className={`range-chip range-chip-filter ${openFilterMenu === "landlordIds" ? "range-chip-filter-active" : ""}`}
              onClick={() =>
                setOpenFilterMenu((prev) => {
                  const next = prev === "landlordIds" ? null : "landlordIds";
                  if (next === "landlordIds") setLandlordSearchTerm("");
                  return next;
                })
              }
            >
              <span>
                {selectedFilters.landlordIds.length
                  ? `${selectedFilters.landlordIds.length} selected`
                  : "All landlords"}
              </span>
            </button>
            {openFilterMenu === "landlordIds" && (
              <div className="tenant-filter-dropdown">
                <div className="tenant-filter-search-wrap">
                  <input
                    type="text"
                    className="tenant-filter-search-input"
                    placeholder="Search landlord name or email..."
                    value={landlordSearchTerm}
                    onChange={(e) => setLandlordSearchTerm(e.target.value)}
                  />
                </div>
                <div className="tenant-filter-dropdown-list">
                  {filteredLandlords.map((landlord) => (
                    <label key={landlord.id} className="tenant-filter-option">
                      <input
                        type="checkbox"
                        checked={selectedFilters.landlordIds.includes(landlord.id)}
                        onChange={() => toggleMultiValue("landlordIds", landlord.id)}
                      />
                      <span className="tenant-filter-option-content">
                        <span className="tenant-filter-option-title">{landlord.name}</span>
                        {landlord.email && (
                          <span className="tenant-filter-option-subtitle">{landlord.email}</span>
                        )}
                      </span>
                    </label>
                  ))}
                  {!filteredLandlords.length && (
                    <div className="tenant-filter-empty">No landlords found</div>
                  )}
                </div>
                <div className="tenant-filter-actions">
                  <button type="button" onClick={() => clearFilter("landlordIds")}>
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
       <div className="tenant-filter-chip-row mb-4">
        <div>
          {filters.datePreset === "custom" && (
            <div className="custom-dates">
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              className="date-input"
              />
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
              className="date-input"
              />
          </div>
        )}
        </div>
          {selectedFilters.employmentStatuses.map((item) => (
            <button
              key={`employment-${item}`}
              type="button"
              className="tenant-filter-chip"
              onClick={() => toggleMultiValue("employmentStatuses", item)}
            >
              {item} <X size={12} />
            </button>
          ))}
          {selectedFilters.nationalities.map((item) => (
            <button
              key={`nationality-${item}`}
              type="button"
              className="tenant-filter-chip"
              onClick={() => toggleMultiValue("nationalities", item)}
            >
              {item} <X size={12} />
            </button>
          ))}
          {selectedFilters.landlordIds.map((item) => {
            const landlord = filterOptions.landlords.find((ll) => ll.id === item);
            const name = landlord?.name || item;
            return (
              <button
                key={`landlord-${item}`}
                type="button"
                className="tenant-filter-chip"
                onClick={() => toggleMultiValue("landlordIds", item)}
              >
                {name} <X size={12} />
              </button>
            );
          })}
        </div>

      {tenantMetrics.loading ? (
        <div className="metrics-loading">
          {[1, 2, 3, 4].map((key) => (
            <div key={key} className="metric-card skeleton">
              <div className="metric-header">
                <div className="metric-icon skeleton-icon" />
              </div>
              <div className="metric-content">
                <div className="skeleton-title" />
                <div className="skeleton-value" />
                <div className="skeleton-subtitle" />
              </div>
            </div>
          ))}
        </div>
      ) : tenantMetrics.data ? (
        <>
          {/* here */}
          <OnboardingFunnel
            tenantMetrics={tenantMetrics}
            userMetrics={userMetrics}
          />

          {userMetrics.data?.metrics && (
            <div className="user-metrics-section">
              <div className="user-metrics-header">
                <h2>User Metrics - Completed Profiles Only</h2>
              </div>
              <div className="user-metrics-grid">
                <div className="user-metric-tile">
                  <div className="user-metric-title">
                    Avg docs uploaded per profile
                  </div>
                  <div className="user-metric-value">
                    {/* {Number(
                      userMetrics.data.metrics.avg_docs_per_profile || 0,
                    ).toFixed(1)} */}
                    {/* #NOTEL rounding the number */}
                    {Math.round(Number(
                      userMetrics.data.metrics.avg_docs_per_profile || 0,
                    ))}
                  </div>
                  <div className="user-metric-subtitle">
                    completed profiles only
                  </div>
                </div>
                <div className="user-metric-tile">
                  <div className="user-metric-title">Cl@ve usage rate</div>
                  <div className="user-metric-value">
                    {formatRate(userMetrics.data.metrics.clave_usage_rate)}
                  </div>
                  <div className="user-metric-subtitle">
                    of completed profiles
                  </div>
                </div>
                <div className="user-metric-tile">
                  <div className="user-metric-title">Bank connected</div>
                  <div className="user-metric-value">
                    {formatRate(userMetrics.data.metrics.bank_connected_rate)}
                  </div>
                  <div className="user-metric-subtitle">
                    of completed profiles
                  </div>
                </div>
                <div className="user-metric-tile">
                  <div className="user-metric-title">
                    Tenants with guarantor
                  </div>
                  <div className="user-metric-value">
                    {formatRate(
                      userMetrics.data.metrics.tenants_with_guarantor_rate,
                    )}
                  </div>
                  <div className="user-metric-subtitle">
                    of completed profiles
                  </div>
                </div>
              </div>
            </div>
          )}

            <TenantListWithMetrics externalFilters={selectedFilters} />
        </>
      ) : (
        <div className="empty-state">
          <p>No tenant metric data available</p>
        </div>
      )}
    </div>
  );
};

export default TenantMetrics;

function OnboardingFunnel({ tenantMetrics, userMetrics }) {
  console.log("tenantMetrics", tenantMetrics);
  const onboarding = tenantMetrics.data.onboarding || {};
  const funnel = onboarding.funnel || {};
  const totalLeads = Number(funnel.reached_step_1?.count || 0);
  const safeLeads = totalLeads || 1;

  const funnelSteps = [
    {
      key: "leads",
      label: "Leads",
      count: totalLeads,
      percentage: funnel.reached_step_1?.percentage || 0,
      median_time_seconds: Number(funnel.reached_step_1?.median_time_seconds || 0),
    },
    {
      key: "basic",
      label: "Basic Info",
      count: Number(funnel.passed_step_1?.count || 0),
      percentage: funnel.passed_step_1?.percentage || 0,
      median_time_seconds: Number(funnel.passed_step_1?.median_time_seconds || 0),
    },
    {
      key: "docs",
      label: "Docs",
      count: Number(funnel.passed_step_2?.count || 0),
      percentage: funnel.passed_step_2?.percentage || 0,
      median_time_seconds: Number(funnel.passed_step_2?.median_time_seconds || 0),
    },
    {
      key: "kyc",
      label: "KYC",
      count: Number(funnel.passed_step_3?.count || 0),
      percentage: funnel.passed_step_3?.percentage || 0,
      median_time_seconds: Number(funnel.passed_step_3?.median_time_seconds || 0),
    },
    {
      key: "profile",
      label: "Profile Visible",
      count: Number(funnel.completed_profile?.count || 0),
      percentage: funnel.completed_profile?.percentage || 0,
      median_time_seconds: Number(funnel.completed_profile?.median_time_seconds || 0),
    },
  ];

  const withStats = funnelSteps.map((step, index) => {
    const prev = index > 0 ? funnelSteps[index - 1].count : step.count;
    const drop =
      index === 0 || prev <= 0 ? 0 : ((step.count - prev) / prev) * 100;
    return { ...step, conversion: step.percentage, drop, prev };
  });

  const dropCandidates = withStats.slice(1).map((step) => ({
    label: step.label,
    dropAbs: Math.abs(Math.min(step.drop, 0)),
  }));
  const biggestDrop = dropCandidates.reduce(
    (max, current) => (current.dropAbs > max.dropAbs ? current : max),
    { label: "-", dropAbs: 0 },
  );
  const endToEndRate = funnel.completed_profile?.percentage || 0;
  const profileVisibleCount = Number(funnel.completed_profile?.count || 0);
  const getConversionColor = (value) => {
    if (value >= 80) return "#3f8ddf";
    if (value >= 60) return "#2fa66f";
    if (value >= 40) return "#e6a33a";
    return "#e6a33a";
  };
  const getDropColor = (dropValue, isBaseline) => {
    if (isBaseline) return "#6e6e6e";
    const loss = Math.abs(Math.min(dropValue, 0));
    if (loss >= 45) return "#c77b7b";
    if (loss >= 25) return "#d09439";
    return "#8f7748";
  };

  return (
    <>
      <div className="onboarding-funnel-section">
        <div className="onboarding-funnel-header">
          <h2>Onboarding Funnel</h2>
        </div>
        <div className="onboarding-funnel-table">
          {withStats.map((step) => {
            const conversionColor = getConversionColor(step.conversion);
            const dropColor = getDropColor(step.drop, step.key === "leads");
            return (
              <div className="funnel-step-column" key={step.key}>
                <div className="funnel-step-column-header">{step.label}</div>
                <div className="funnel-step-column-body">
                  <div className="funnel-step-value">{step.count}</div>
                  <div
                    className="funnel-step-conversion"
                    style={{ color: conversionColor }}
                  >
                    {Math.round(step.conversion)}%
                  </div>
                </div>
                <div className="funnel-step-bar">
                  <div
                    className="funnel-step-bar-fill"
                    style={{
                      width: `${Math.max(0, Math.min(100, step.conversion))}%`,
                      backgroundColor: conversionColor,
                    }}
                  />
                </div>
                <div className="funnel-step-drop" style={{ color: dropColor }}>
                  {step.key === "leads"
                    ? "baseline"
                    : `${step.drop < 0 ? "-" : "+"}${Math.round(Math.abs(step.drop))}% drop`}
                </div>
                <div className="funnel-step-median">
                  {step.key === "leads"
                    ? "—"
                    : `median — ${formatMedianTime(step?.median_time_seconds)}`}
                </div>
              </div>
            );
          })}
        </div>

        <div className="onboarding-funnel-notes">
          <p className="funnel-note">
            <span className="funnel-note-dot funnel-note-dot-blue" />
            <strong>End-to-end completion:</strong> {profileVisibleCount} of{" "}
            {totalLeads} leads reached a visible profile -{" "}
            <strong>{endToEndRate.toFixed(1)}%</strong>. Drop % is relative to
            the previous step.
          </p>
          <p className="funnel-note">
            <span className="funnel-note-dot funnel-note-dot-amber" />
            <strong>Median time</strong> is cumulative from lead capture.
            Biggest friction: {biggestDrop.label.toUpperCase()} -{" "}
            {biggestDrop.dropAbs.toFixed(0)}% drop.
          </p>
        </div>
      </div>
    </>
  );
}
